<svelte:options runes={true} />

<script lang="ts">
  import IconChevronDown from "~icons/dusk/chevron-down";
  import type { SortOption } from "~/lib/bloomreach/types";
  import type { Filter, FilterState } from "~/lib/filters/types";
  import { removeFilter } from "~/lib/filters/utils";
  import type { Component } from "svelte";
  import type { Attachment } from "svelte/attachments";
  import ActiveFilters from "~/components/filters/ActiveFilters/ActiveFilters.svelte";

  interface Props {
    filters: Filter[];
    sortOptions: SortOption[];
    activeFilters?: FilterState;
    loading?: boolean;
    error?: boolean;
    resultCount?: number;
    sort?: string;
    onChange?: (activeFilters: FilterState) => void;
    onSortChange?: (value: string) => void;
    onReset?: () => void;
    class?: string;
  }

  let {
    filters,
    sortOptions,
    activeFilters = $bindable({}),
    loading = false,
    error = false,
    resultCount,
    sort = "",
    onChange,
    onSortChange,
    onReset,
    class: className = "",
  }: Props = $props();

  // Synchronous init from viewport width avoids a blank frame on first paint.
  // ResizeObserver later corrects this if the filter bar container is narrower than the viewport.
  let isDesktop = $state(typeof window !== "undefined" && window.innerWidth > 500);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let DesktopFiltersComponent = $state<Component<any> | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let MobileFiltersComponent = $state<Component<any> | null>(null);
  type LoadStatus = "idle" | "loading" | "loaded" | "failed";
  let desktopStatus = $state<LoadStatus>("idle");
  let mobileStatus = $state<LoadStatus>("idle");

  // Load DesktopFilters/MobileFilters whenever isDesktop settles into the
  // matching state for the first time (F11 — neither pays for the other's
  // bundle or mounts the other's hidden DOM). A failed download shows "Filters
  // unavailable" rather than nothing; sort and the grid keep working.
  // Runs inside $effect so the reactive write back to $state triggers a re-render.
  $effect(() => {
    if (isDesktop && desktopStatus === "idle") {
      desktopStatus = "loading";
      import("./DesktopFilters/DesktopFilters.svelte")
        .then((m) => {
          DesktopFiltersComponent = m.default;
          desktopStatus = "loaded";
        })
        .catch((error: unknown) => {
          console.error("[Aurora] Failed to load DesktopFilters:", error);
          desktopStatus = "failed";
        });
    }
  });

  $effect(() => {
    if (!isDesktop && mobileStatus === "idle") {
      mobileStatus = "loading";
      import("./MobileFilters/MobileFilters.svelte")
        .then((m) => {
          MobileFiltersComponent = m.default;
          mobileStatus = "loaded";
        })
        .catch((error: unknown) => {
          console.error("[Aurora] Failed to load MobileFilters:", error);
          mobileStatus = "failed";
        });
    }
  });

  // ResizeObserver refines isDesktop using the container's actual inline-size,
  // matching the CSS @container (width > 500px) query exactly.
  const observeIsDesktop: Attachment = (element) => {
    const observer = new ResizeObserver(([entry]) => {
      isDesktop = (entry.contentBoxSize[0]?.inlineSize ?? 0) > 500;
    });
    observer.observe(element);
    return () => observer.disconnect();
  };

  const showSkeleton = $derived(loading && filters.length === 0);
  // The filter chips also wait on this layout's component chunk.
  const currentStatus = $derived(isDesktop ? desktopStatus : mobileStatus);
  const showFiltersSkeleton = $derived(
    showSkeleton || currentStatus === "idle" || currentStatus === "loading",
  );
  const sortDisabled = $derived(resultCount === undefined);

  function handleFilterRemove(filterId: string, value?: string) {
    activeFilters = removeFilter(activeFilters, filterId, value);
    onChange?.(activeFilters);
  }

  function handleReset() {
    activeFilters = {};
    // onChange propagates the cleared state; onReset is a separate signal for the
    // parent to handle (e.g. analytics, URL cleanup) distinct from a filter change.
    onChange?.({});
    onReset?.();
  }
</script>

<div class="da-filter-bar {className}" {@attach observeIsDesktop}>
  {#if error}
    <p class="da-filter-bar__error" role="alert">Filters unavailable</p>
  {:else}
    <div class="da-filter-bar__grid">
      <svelte:boundary
        onerror={(e) => console.error("[Aurora] FilterBar [filters] render failed:", e)}
      >
        {#if showFiltersSkeleton}
          <div
            class="da-filter-bar__skeleton-area"
            role="status"
            aria-busy="true"
            aria-label="Loading filters"
          >
            {#each { length: 6 }, i (i)}
              <div class="da-filter-bar__skeleton-chip" aria-hidden="true"></div>
            {/each}
          </div>
        {:else}
          <div class="da-filter-bar__desktop-area">
            {#if DesktopFiltersComponent}
              <DesktopFiltersComponent {filters} bind:activeFilters {onChange} />
            {:else if desktopStatus === "failed"}
              <p class="da-filter-bar__load-failed" role="alert">Filters unavailable</p>
            {/if}
          </div>
          <div class="da-filter-bar__mobile-area">
            {#if MobileFiltersComponent}
              <MobileFiltersComponent {filters} bind:activeFilters {onChange} {onReset} />
            {:else if mobileStatus === "failed"}
              <p class="da-filter-bar__load-failed" role="alert">Filters unavailable</p>
            {/if}
          </div>
        {/if}

        {#snippet failed()}
          <p class="da-filter-bar__error" role="alert">Filters unavailable</p>
        {/snippet}
      </svelte:boundary>

      <div class="da-filter-bar__sort-wrapper">
        {#if showSkeleton}
          <div class="da-filter-bar__sort-skeleton" aria-hidden="true"></div>
        {:else}
          <select
            class="da-filter-bar__sort"
            value={sort}
            onchange={(e) => onSortChange?.(e.currentTarget.value)}
            aria-label="Sort products"
            disabled={sortDisabled}
          >
            {#each sortOptions as option (option.value)}
              <option value={option.value}>{option.label}</option>
            {/each}
          </select>
          <div class="da-filter-bar__sort-chevron" aria-hidden="true">
            <IconChevronDown width="16" height="16" />
          </div>
        {/if}
      </div>

      <div class="da-filter-bar__selected-filters-row">
        <svelte:boundary
          onerror={(e) => console.error("[Aurora] FilterBar [active-filters] render failed:", e)}
        >
          <ActiveFilters
            {filters}
            {activeFilters}
            onRemove={handleFilterRemove}
            onReset={handleReset}
          />
          {#snippet failed()}{/snippet}
        </svelte:boundary>
      </div>

      {#if showSkeleton}
        <p class="da-filter-bar__results da-filter-bar__results--skeleton" aria-hidden="true"></p>
      {:else if resultCount !== undefined}
        <p class="da-filter-bar__results">{resultCount} results</p>
      {/if}
    </div>
  {/if}
</div>

<style>
  @layer aurora {
    /* Outer element is the container context only — no layout here */
    .da-filter-bar {
      container-type: inline-size;
      overflow-x: clip;
    }

    /* Inner grid is a valid @container descendant so grid-template-areas can change */
    .da-filter-bar__grid {
      display: grid;
      align-items: center;
      padding-inline: var(--da-space-4);
      column-gap: var(--da-space-2);
      grid-template-columns: 1fr 1fr;
      grid-template-areas:
        "filter-trigger   sort-select"
        "selected-filters selected-filters"
        "results          results";
      max-width: 192rem;
      margin-inline: auto;
    }

    /* Sand background spanning the full primary row */
    .da-filter-bar__grid::before {
      content: "";
      grid-column: 1 / -1;
      grid-row: 1;
      align-self: stretch;
      background: var(--da-color-brand-sand-500);
      margin-inline: calc(-1 * var(--da-space-4));
    }

    /* Desktop filter wrapper — hidden on mobile */
    .da-filter-bar__desktop-area {
      grid-area: filter-trigger;
      display: none;
    }

    /* Mobile filter wrapper — visible on mobile */
    .da-filter-bar__mobile-area {
      grid-area: filter-trigger;
      padding-block: var(--da-space-3);
      padding-inline-start: 0;
    }

    /* Sort select wrapper */
    .da-filter-bar__sort-wrapper {
      grid-area: sort-select;
      position: relative;
      padding-block: var(--da-space-3);
    }

    .da-filter-bar__sort {
      appearance: none;
      -webkit-appearance: none;
      width: 100%;
      padding-inline-start: var(--da-space-3);
      padding-inline-end: calc(var(--da-space-3) + 16px + var(--da-space-1));
      padding-block: var(--da-space-2);
      background: var(--da-color-brand-sand-100);
      border: 1px solid var(--da-color-brand-sand-900);
      border-radius: var(--da-radius-lg);
      box-shadow: var(--da-shadow-xs);
      font-family: var(--da-font-family-primary);
      font-size: var(--da-font-size-xxs);
      line-height: var(--da-line-height-xs);
      font-weight: var(--da-font-weight-medium);
      color: var(--da-color-charcoal-500);
      cursor: pointer;
    }

    .da-filter-bar__sort-chevron {
      position: absolute;
      right: var(--da-space-3);
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      color: var(--da-color-charcoal-500);
      display: flex;
      align-items: center;
    }

    /* Results count — spans full secondary row on mobile, centered */
    .da-filter-bar__results {
      grid-area: results;
      margin: 0;
      text-align: center;
      padding-block-start: var(--da-space-4);
      font-family: var(--da-font-family-primary);
      font-size: var(--da-font-size-xs);
      font-weight: var(--da-font-weight-medium);
      color: var(--da-color-charcoal-400);
      text-transform: uppercase;
    }

    /* Error state — spans full primary row */
    .da-filter-bar__error {
      grid-column: 1 / -1;
      grid-row: 1;
      align-self: stretch;
      margin: 0;
      padding: var(--da-space-3) 0;
      font-size: var(--da-font-size-xs);
      color: var(--da-color-charcoal-500);
    }

    /* Its layout's filter chunk failed to load */
    .da-filter-bar__load-failed {
      margin: 0;
      font-size: var(--da-font-size-xs);
      color: var(--da-color-charcoal-500);
    }

    /* Skeleton area — col 1 (filter-trigger) on mobile */
    .da-filter-bar__skeleton-area {
      grid-area: filter-trigger;
      align-self: stretch;
      display: flex;
      gap: var(--da-space-2);
      padding-block: var(--da-space-3);
    }

    .da-filter-bar__skeleton-chip {
      flex: 1;
      height: calc((var(--da-space-2) * 2) + 16px + 2px);
      border-radius: var(--da-radius-lg);
      background: var(--da-color-brand-sand-900);
      opacity: 0.25;
      animation: da-filter-bar-pulse 1.4s ease-in-out infinite;
    }

    /* Sort skeleton — matches sort select dimensions */
    .da-filter-bar__sort-skeleton {
      width: 100%;
      height: calc((var(--da-space-2) * 2) + 16px + 2px);
      border-radius: var(--da-radius-lg);
      background: var(--da-color-brand-sand-900);
      opacity: 0.25;
      animation: da-filter-bar-pulse 1.4s ease-in-out infinite;
    }

    @keyframes da-filter-bar-pulse {
      0%,
      100% {
        opacity: 0.25;
      }
      50% {
        opacity: 0.45;
      }
    }

    /* Selected filters row — spans full width, counteracts grid side-padding on mobile */
    .da-filter-bar__selected-filters-row {
      grid-area: selected-filters;
      margin-inline: calc(-1 * var(--da-space-4));
    }

    /* ── Desktop layout ────────────────────────────────────────────────────── */

    @container (width > 500px) {
      .da-filter-bar__grid {
        padding-inline: 0;
        column-gap: 0;
        grid-template-columns: 1fr auto;
        grid-template-areas:
          "desktop-filters  desktop-filters"
          "selected-filters selected-filters"
          "results          sort-select";
      }

      .da-filter-bar__grid::before {
        margin-inline: calc(50% - 50vw);
      }

      .da-filter-bar__selected-filters-row {
        margin-inline: 0;
      }

      /* Desktop filter area: show as flex row */
      .da-filter-bar__desktop-area {
        grid-area: desktop-filters;
        display: flex;
        align-items: center;
        padding: var(--da-space-3) var(--da-space-4);
      }

      /* Mobile filter area: hide */
      .da-filter-bar__mobile-area {
        display: none;
      }

      /* Sort moves to the secondary row on desktop */
      .da-filter-bar__sort-wrapper {
        grid-area: sort-select;
        padding-block: var(--da-space-2);
        padding-inline: var(--da-space-8) var(--da-space-8);
      }

      .da-filter-bar__sort {
        width: 100%;
        max-width: 16rem;
        padding-inline-start: var(--da-space-4);
        padding-inline-end: calc(var(--da-space-4) + 20px);
        padding-block: var(--da-space-px-10);
        background: var(--da-color-brand-sand-25);
        border-radius: var(--da-radius-xl);
        font-size: var(--da-font-size-xs);
        line-height: normal;
      }

      .da-filter-bar__sort-chevron {
        right: calc(var(--da-space-8) + var(--da-space-3));
      }

      /* Results: left-aligned in the secondary row */
      .da-filter-bar__results {
        text-align: left;
        padding-block: var(--da-space-2);
        padding-inline-start: var(--da-space-8);
        color: var(--da-color-charcoal-400);
        text-transform: uppercase;
      }

      /* Error text: pad to match filter area inset so it doesn't sit flush */
      .da-filter-bar__error {
        padding-inline: var(--da-space-4);
      }

      /* Skeleton area: span full top row, matching desktop filter area padding */
      .da-filter-bar__skeleton-area {
        grid-area: desktop-filters;
        padding-inline: var(--da-space-4);
      }

      /* Desktop: show all skeleton chips */
      .da-filter-bar__skeleton-chip {
        flex: 0 0 auto;
        width: 120px;
        height: calc((var(--da-space-2) * 2) + var(--da-line-height-sm) + 2px);
      }

      /* Desktop sort skeleton: fixed width drives the auto grid column the same way
         max-width on the real select does (36px height = 3.6rem at 10px root) */
      .da-filter-bar__sort-skeleton {
        width: 16rem;
        height: 3.6rem;
        border-radius: var(--da-radius-xl);
      }
    }
  }

  /* Unlayered overrides: the Shopify theme's base.css has an unlayered rule
     `div:empty { display:none }` that hides CSS-only skeleton placeholders.
     Scoped selectors here win on specificity (0,2,0) vs (0,1,1).
     nth-child visibility is also here so it isn't overridden by the display:block above. */
  .da-filter-bar__skeleton-chip {
    display: block;
  }

  /* Mobile: show only the first chip — col 2 uses the sort skeleton */
  .da-filter-bar__skeleton-chip:nth-child(n + 2) {
    display: none;
  }

  /* Desktop: restore all chips */
  @container (width > 500px) {
    .da-filter-bar__skeleton-chip:nth-child(n + 2) {
      display: block;
    }
  }

  .da-filter-bar__sort-skeleton {
    display: block;
  }

  /* Results skeleton: ::after creates the visible bar inside the paragraph strut */
  .da-filter-bar__results--skeleton::after {
    content: "";
    display: inline-block;
    width: 4.5rem;
    height: 0.875rem;
    border-radius: var(--da-radius-sm);
    background: var(--da-color-brand-sand-900);
    opacity: 0.25;
    animation: da-filter-bar-pulse 1.4s ease-in-out infinite;
    vertical-align: middle;
  }
</style>
